/**
 * Conversation Item Component
 * 
 * Displays a conversation in the list with last message preview,
 * unread count badge, and online status indicator.
 */

import React, { useState } from 'react';
import { Avatar, Badge, Typography, Button } from 'antd';
import { TeamOutlined, SettingOutlined, BellOutlined, PushpinOutlined } from '@ant-design/icons';
import styled from '@emotion/styled';
import DOMPurify from 'dompurify';
import type { Conversation } from '@municipality/types';
import { format } from 'date-fns';
import { ConversationSettingsModal } from './ConversationSettingsModal';
import { useResponsive } from '@municipality/ui';

const { Text } = Typography;

/**
 * Get initials from a name (first letter of first and last name)
 */
const getInitials = (name: string): string => {
  if (!name) return '?';
  
  const parts = name.trim().split(' ');
  if (parts.length === 1) {
    return parts[0].charAt(0).toUpperCase();
  }
  
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
};

/**
 * Generate a consistent color from a string (name)
 */
const getAvatarColor = (name: string): string => {
  const colors = [
    '#1890ff', '#52c41a', '#faad14', '#f5222d', '#722ed1',
    '#13c2c2', '#eb2f96', '#fa8c16', '#a0d911', '#2f54eb'
  ];
  
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  
  return colors[Math.abs(hash) % colors.length];
};

/**
 * Sanitize message preview to prevent XSS attacks
 */
const sanitizePreview = (content: string): string => {
  return DOMPurify.sanitize(content, {
    ALLOWED_TAGS: [],  // No HTML tags allowed
    ALLOWED_ATTR: [],
    KEEP_CONTENT: true,
  });
};

interface ConversationItemProps {
  conversation: Conversation;
  isActive: boolean;
  currentUserId: string;
  onClick: () => void;
  onMute?: (conversationId: string, isMuted: boolean) => Promise<void>;
  onArchive?: (conversationId: string, isArchived: boolean) => Promise<void>;
  onPin?: (conversationId: string, isPinned: boolean) => Promise<void>;
}

const ConversationContainer = styled('div', {
  shouldForwardProp: (prop) => prop !== 'isActive',
})<{ isActive: boolean }>`
  display: flex;
  align-items: center;
  padding: 12px 16px;
  cursor: pointer;
  transition: background-color 0.2s;
  background-color: ${(props) => (props.isActive ? '#e6f7ff' : 'transparent')};
  border-radius: 8px;
  margin-bottom: 4px;

  &:hover {
    background-color: ${(props) => (props.isActive ? '#e6f7ff' : '#f5f5f5')};
  }
`;

const ConversationContent = styled.div`
  flex: 1;
  min-width: 0;
  margin-left: 12px;
`;

const ConversationHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 4px;
`;

const ConversationName = styled(Text, {
  shouldForwardProp: (prop) => prop !== 'hasUnread',
})<{ hasUnread: boolean }>`
  font-weight: ${(props) => (props.hasUnread ? 600 : 400)};
  font-size: 14px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const LastMessagePreview = styled(Text, {
  shouldForwardProp: (prop) => prop !== 'hasUnread',
})<{ hasUnread: boolean }>`
  font-size: 13px;
  color: ${(props) => (props.hasUnread ? '#000' : '#666')};
  font-weight: ${(props) => (props.hasUnread ? 500 : 400)};
  display: block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Timestamp = styled(Text)`
  font-size: 12px;
  color: #999;
  white-space: nowrap;
`;

const ConversationAvatar = styled(Avatar, {
  shouldForwardProp: (prop) => prop !== '$bgColor',
})<{ $bgColor?: string }>`
  background-color: ${(props) => props.$bgColor || 'transparent'} !important;
  color: #fff !important;
  font-weight: 500 !important;
  font-size: 13px !important;
`;

const AvatarWrapper = styled.div`
  position: relative;
`;

const SettingsButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== '$isMobileOrTablet',
})<{ $isMobileOrTablet: boolean }>`
  opacity: ${({ $isMobileOrTablet }) => ($isMobileOrTablet ? 1 : 0)};
  transition: opacity 0.2s;
  padding: 4px;
  width: 24px;
  height: 24px;
  min-width: 24px;
  border: none;
  box-shadow: none;
  color: #999;
  
  &:hover {
    color: #1890ff;
    background-color: transparent;
  }
`;

const ConversationContainerWithHover = styled(ConversationContainer, {
  shouldForwardProp: (prop) => prop !== '$isMobileOrTablet',
})<{ $isMobileOrTablet: boolean }>`
  position: relative;
  
  ${({ $isMobileOrTablet }) =>
    !$isMobileOrTablet &&
    `
    &:hover .settings-button {
      opacity: 1;
    }
  `}
`;

const IconContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  margin-left: 8px;
`;

const IndicatorIcon = styled.span`
  font-size: 12px;
  color: #999;
`;

export const ConversationItem: React.FC<ConversationItemProps> = ({
  conversation,
  isActive,
  currentUserId,
  onClick,
  onMute,
  onArchive,
  onPin,
}) => {
  const { isMobile, isTablet } = useResponsive();
  const isMobileOrTablet = isMobile || isTablet;
  const [settingsModalOpen, setSettingsModalOpen] = useState(false);
  const unreadCount = conversation.unreadCount[currentUserId] || 0;
  const hasUnread = unreadCount > 0;
  const isMuted = conversation.isMuted?.[currentUserId] || false;
  const isPinned = conversation.pinnedBy?.includes(currentUserId) || false;

  // Get conversation name (for direct chats, show other user's name)
  const getConversationName = () => {
    if (conversation.type === 'group') {
      return conversation.name || 'Group Chat';
    }
    
    // For direct chats, show the other participant's name
    const otherParticipantId = conversation.participantIds.find(
      (id) => id !== currentUserId
    );
    
    if (otherParticipantId) {
      const participant = conversation.participantDetails[otherParticipantId];
      return participant?.name || 'Unknown User';
    }
    
    return 'Chat';
  };

  // Get avatar for display
  const getAvatar = () => {
    if (conversation.type === 'group') {
      return conversation.avatar ? (
        <ConversationAvatar src={conversation.avatar} size={30} />
      ) : (
        <ConversationAvatar icon={<TeamOutlined />} size={30} $bgColor="#1890ff" />
      );
    }
    
    // For direct chats, show other user's avatar with initials
    const otherParticipantId = conversation.participantIds.find(
      (id) => id !== currentUserId
    );
    
    if (otherParticipantId) {
      const participant = conversation.participantDetails[otherParticipantId];
      const initials = getInitials(participant?.name || 'Unknown');
      const color = getAvatarColor(participant?.name || 'Unknown');
      
      return (
        <AvatarWrapper>
          <ConversationAvatar 
            src={participant?.avatar} 
            size={30}
            $bgColor={participant?.avatar ? undefined : color}
          >
            {!participant?.avatar && initials}
          </ConversationAvatar>
          {/* TODO: Add online indicator based on presence */}
        </AvatarWrapper>
      );
    }
    
    return <ConversationAvatar size={30} $bgColor="#999">?</ConversationAvatar>;
  };

  const formatTime = (date: Date | undefined) => {
    if (!date) return '';
    
    try {
      return format(date, 'dd/MM/yyyy');
    } catch {
      return '';
    }
  };

  // Sanitize last message preview for XSS protection
  const lastMessagePreview = conversation.lastMessage 
    ? sanitizePreview(conversation.lastMessage.content)
    : '';

  const handleSettingsClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setSettingsModalOpen(true);
  };

  return (
    <>
      <ConversationContainerWithHover isActive={isActive} onClick={onClick} $isMobileOrTablet={isMobileOrTablet}>
        <Badge count={unreadCount} offset={[-5, 5]}>
          {getAvatar()}
        </Badge>
        
        <ConversationContent>
          <ConversationHeader>
            <div style={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0 }}>
              <ConversationName hasUnread={hasUnread}>
                {getConversationName()}
              </ConversationName>
              <IconContainer>
                {isPinned && (
                  <IndicatorIcon title="Pinned">
                    <PushpinOutlined />
                  </IndicatorIcon>
                )}
                {isMuted && (
                  <IndicatorIcon title="Muted">
                    <BellOutlined />
                  </IndicatorIcon>
                )}
              </IconContainer>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {conversation.lastMessage && (
                <Timestamp>
                  {formatTime(conversation.lastMessage.timestamp)}
                </Timestamp>
              )}
              {(onMute || onArchive || onPin) && (
                <SettingsButton
                  className="settings-button"
                  icon={<SettingOutlined />}
                  onClick={handleSettingsClick}
                  type="text"
                  size="small"
                  $isMobileOrTablet={isMobileOrTablet}
                />
              )}
            </div>
          </ConversationHeader>
        
        {conversation.lastMessage && (
          <LastMessagePreview hasUnread={hasUnread}>
            {conversation.lastMessage.senderName === currentUserId
              ? 'You: '
              : ''}
            {lastMessagePreview}
          </LastMessagePreview>
        )}
        
        {(() => {
          // Filter out current user from typing users (only show when others are typing)
          const otherTypingUsers = conversation.typingUsers.filter(
            (userId) => userId !== currentUserId
          );
          
          if (otherTypingUsers.length === 0) return null;
          
          return (
            <LastMessagePreview hasUnread={false}>
              <Text type="secondary" italic>
                {otherTypingUsers.length === 1
                  ? 'Typing...'
                  : `${otherTypingUsers.length} people typing...`}
              </Text>
            </LastMessagePreview>
          );
        })()}
      </ConversationContent>
    </ConversationContainerWithHover>

    {(onMute || onArchive || onPin) && (
      <ConversationSettingsModal
        open={settingsModalOpen}
        conversation={conversation}
        currentUserId={currentUserId}
        onClose={() => setSettingsModalOpen(false)}
        onMute={onMute || (async () => {})}
        onArchive={onArchive || (async () => {})}
        onPin={onPin || (async () => {})}
      />
    )}
    </>
  );
};

